import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppLayout } from './components/layout/AppLayout';
import { AuthPage } from './components/auth/AuthPage';
import { DashboardPage } from './pages/DashboardPage';
import { SessionLoggerPage } from './pages/SessionLoggerPage';
import { DrillsPage } from './pages/DrillsPage';
import { HistoryPage } from './pages/HistoryPage';
import { AdminPage } from './pages/AdminPage';
import { ProgressPage } from './pages/ProgressPage';
import { SubscribePage } from './pages/SubscribePage';
import { AccessGate } from './components/ui/AccessGate';
import { LoadingSpinner } from './components/ui/Cards';
import { LanguageProvider } from './contexts/LanguageContext';
import './styles/globals.css';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!profile?.isAdmin) return <Navigate to="/dashboard" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login"     element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/"          element={<Navigate to="/dashboard" replace />} />

      {/* Dashboard — always accessible, shows upgrade prompts inline */}
      <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />

      {/* Subscribe — must always be accessible, even for expired users */}
      <Route path="/subscribe" element={<PrivateRoute><SubscribePage /></PrivateRoute>} />

      {/* Gated routes — require active subscription or valid trial */}
      <Route path="/session"   element={<PrivateRoute><AccessGate><SessionLoggerPage /></AccessGate></PrivateRoute>} />
      <Route path="/drills"    element={<PrivateRoute><AccessGate><DrillsPage /></AccessGate></PrivateRoute>} />
      <Route path="/history"   element={<PrivateRoute><AccessGate><HistoryPage /></AccessGate></PrivateRoute>} />
      <Route path="/progress"  element={<PrivateRoute><AccessGate><ProgressPage /></AccessGate></PrivateRoute>} />

      {/* Admin — separate gate, isAdmin only */}
      <Route path="/admin"     element={<AdminRoute><AdminPage /></AdminRoute>} />

      <Route path="*"          element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <AppRoutes />
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}